<script lang="ts">
import { defineComponent, inject, ref, reactive, computed, watch } from 'vue'
import stateInitializer from '@/models/stateInitializer'
import SectionHeadingForHome from '@/components/heading/SectionHeadingForHome.vue'
import LayoutContentsCardsCols from '@/components/contents/LayoutContentsCardsCols.vue'
import LayoutContentsCardsRows from '@/components/contents/LayoutContentsCardsRows.vue'
import ToastItem from '@/components/toast/ToastItem.vue'
import CarouselHome from '@/components/carousel/CarouselHome.vue'
import { mypagePath, themePath, storyPath } from '@/generated/railsRoutes'
import AD_BANNER_BIZHINT_PC from '@/images/banner/banner-featured-bizhint_01_pc.png'
import AD_BANNER_BIZHINT_SP from '@/images/banner/banner-featured-bizhint_01_sp.png'
import { useUserStore } from '@/stores/user'
import dateUtil from '@/utilities/date'

export default defineComponent({
  name: 'IndexHero',

  components: {
    SectionHeadingForHome,
    LayoutContentsCardsCols,
    LayoutContentsCardsRows,
    ToastItem,
    CarouselHome,
  },

  setup() {
    // constants
    const THEMES = inject<Record<string, string>>('THEMES') || {}
    const ADVERTISEMENT_BIZHINT_URL = inject<Record<string, string>>('EXTERNAL_URL')?.ad_document_request_url || ''

    const IMAGE_OPTIONS = {
      MY_TOPICS: {
        width: {
          pc: 187,
          sp: 187,
        },
        height: {
          pc: 98,
          sp: 98,
        },
      },
    } as const

    // store
    const userStore = useUserStore()

    interface PromotionItem {
      thumbImageUrl: string,
      to: string,
      description: string,
      isPr: boolean,
    }

    interface State {
      hasFollowContents: boolean,
      promotions: Array<PromotionItem>,
      myTopics: Array<ReportItem | ArticleItem>,
      featuredTheme: {
        id: Maybe<number>,
        stories: Array<StoryItem>,
      }
    }

    const state: State = reactive({
      hasFollowContents: false,
      promotions: [],
      myTopics: [],
      featuredTheme: {
        id: undefined,
        stories: [],
      },
    })

    stateInitializer(state, 'hero')

    // フォロー CTA セクションの表示アニメーション制御用（v-if で DOM ごと削除すると、CSS transition のアニメーションが効かないので、CSS transition 時間分待機してから DOM ごと削除するため）
    const isShowFollowCta = ref(false)

    const localStorageKeyFollowCtaTotalClosed = ref('')
    const localStorageKeyFollowCtaLastClosedAt = ref('')
    const followCtaTotalClosed = ref(0)
    const followCtaLastClosedAt = ref('')

    // 抑制情報のリセット（in-memory ＋ localStorage）
    const resetFollowCtaSuppression = () => {
      followCtaTotalClosed.value = 0
      followCtaLastClosedAt.value = ''
      localStorage.removeItem(localStorageKeyFollowCtaTotalClosed.value)
      localStorage.removeItem(localStorageKeyFollowCtaLastClosedAt.value)
    }

    // 閉じた回数が3回未満なら表示。3回以降の再表示制御（30日経過リセット）は id-watch 側で行う
    const isFollowCtaShowable = computed(() => followCtaTotalClosed.value < 3)

    watch(
      [
        () => userStore.context.isSignedIn,
        () => state.hasFollowContents,
        () => isFollowCtaShowable.value,
      ],
      ([newIsSignedIn, newHasFollowContents, newLocalStorageState]) => {
        isShowFollowCta.value = newIsSignedIn && !newHasFollowContents && newLocalStorageState
      },
      { immediate: true }
    )

    watch(() => userStore.context.id, () => {
      localStorageKeyFollowCtaTotalClosed.value = `bizhint_user_${userStore.context.id}_home_follow_cta_total_closed`
      localStorageKeyFollowCtaLastClosedAt.value = `bizhint_user_${userStore.context.id}_home_follow_cta_last_closed_at`

      // フォロー中（フォローコンテンツあり）の場合は CTA 抑制をリセットする。
      // 全フォロー解除後に、過去の「3回閉じた/30日未満」に関係なく再表示させるため。
      if (state.hasFollowContents) {
        resetFollowCtaSuppression()
        return
      }

      followCtaTotalClosed.value = Number(localStorage.getItem(localStorageKeyFollowCtaTotalClosed.value) || 0)
      followCtaLastClosedAt.value = localStorage.getItem(localStorageKeyFollowCtaLastClosedAt.value) || ''

      // 最後に閉じてから30日以上経過していたら抑制をリセットし、再び3回閉じられるサイクルに戻す
      if (followCtaLastClosedAt.value &&
          dateUtil.differenceInDays(new Date(), new Date(followCtaLastClosedAt.value)) > 30) {
        resetFollowCtaSuppression()
      }
    }, { immediate: true })

    const onCloseFollowCta = () => {
      isShowFollowCta.value = false
      followCtaTotalClosed.value++
      followCtaLastClosedAt.value = new Date().toISOString()
      localStorage.setItem(localStorageKeyFollowCtaTotalClosed.value, followCtaTotalClosed.value.toString())
      localStorage.setItem(localStorageKeyFollowCtaLastClosedAt.value, followCtaLastClosedAt.value)
    }

    return {
      // images
      AD_BANNER_BIZHINT_PC,
      AD_BANNER_BIZHINT_SP,

      // constants
      THEMES,
      ADVERTISEMENT_BIZHINT_URL,
      IMAGE_OPTIONS,

      // routes
      mypagePath,
      themePath,
      storyPath,

      // store
      userStore,

      // data
      state,
      isShowFollowCta,

      // methods
      onCloseFollowCta,
    }
  },
})
</script>

<template>
  <section class="section">
    <transition name="is-accordion">
      <section
        v-if="isShowFollowCta"
        class="f-section__followCta">
        <div class="f-section__followCta--body">
          <ToastItem
            message-type="notice"
            :max-line="0"
            @close="onCloseFollowCta">
            フォローを追加すると、マイ・トピックスで最新の関連情報をまとめ読みできます。<br class="br u-media__ltPH">
            <a
              class="a u-text__block u-action__forward"
              :href="mypagePath()">
              マイ・トピックスを設定する
            </a>
          </ToastItem>
        <!-- /.f-section__followCta body --></div>
      <!-- /.f-section__followCta --></section>
    </transition>

    <section
      v-if="state.promotions.length"
      class="f-section__heroPromotion">
      <div class="f-section__heroPromotion--body">
        <CarouselHome
          :items="state.promotions"
          enable-loop>
          <template #default="{ item, index }">
            <a
              class="l-unit__heroPromotion"
              :data-label="!!item.description"
              :href="item.to">
              <div
                v-if="item.description"
                class="l-unit__heroPromotion--header">
                <div class="o-heading__heroPromotion">
                  <p
                    class="o-heading__heroPromotion--item"
                    v-html="item.description" />
                <!-- /.o-heading__heroPromotion --></div>
              <!-- /.l-unit__heroPromotion header --></div>

              <div
                v-if="!!item.thumbImageUrl"
                class="l-unit__heroPromotion--image">
                <img
                  class="img"
                  :src="item.thumbImageUrl"
                  :loading="index === 0 ? undefined : 'lazy'"
                  :fetchpriority="index === 0 ? 'high' : undefined"
                  :alt="item.description"
                  data-is-liquid="true">
              <!-- /.l-unit__heroPromotion image --></div>

              <div
                v-if="item.isPr"
                class="l-unit__heroPromotion--footer">
                <div
                  class="o-label__pr"
                  :data-color="!!item.description ? 'white' : 'gray'">
                  <p class="o-label__pr--item">
                    PR
                  </p>
                <!-- /.o-label__pr --></div>
              <!-- /.l-unit__heroPromotion footer --></div>
            <!-- /.l-unit__heroPromotion --></a>
          </template>
        </CarouselHome>
      <!-- /.f-section__heroPromotion body --></div>
    <!-- /.f-section__heroPromotion --></section>

    <section
      v-if="state.myTopics.length"
      class="f-section__hero">
      <div class="f-section__hero--body">
        <SectionHeadingForHome>
          マイ・トピックス
        </SectionHeadingForHome>

        <LayoutContentsCardsCols
          :items="state.myTopics"
          :cols="{ pc: 5, sp: 1 }"
          :heading-options="{ maxLine: 3 }"
          :image-options="IMAGE_OPTIONS.MY_TOPICS"
          :has-solution-link="false" />

        <p class="p u-text__small u-text__right u-margin__larger">
          <a
            class="a u-action__forward"
            :href="mypagePath()">
            マイ・トピックスをもっと見る
          </a>
        </p>
      <!-- /.f-section__hero body --></div>
    <!-- /.f-section__hero --></section>

    <section class="f-section__adBanner">
      <div class="f-section__adBanner--body">
        <p class="p">
          <a
            class="a"
            :href="ADVERTISEMENT_BIZHINT_URL">
            <picture class="picture">
              <!-- media の値は _variables.sass の $global-width-phablet（728 + 12*2）と一致させる -->
              <source
                class="source"
                media="(max-width: 752px)"
                :srcset="AD_BANNER_BIZHINT_SP">
              <img
                class="img"
                :src="AD_BANNER_BIZHINT_PC"
                alt="売上に繋がるMQL/商談獲得ならビズヒント"
                data-is-liquid="true">
            </picture>
          </a>
        </p>
      <!-- /.f-section__adBanner body --></div>
    <!-- /.f-section__adBanner --></section>

    <section
      v-if="state.featuredTheme.id"
      class="f-section__hero">
      <div class="f-section__hero--body">
        <SectionHeadingForHome>
          {{ THEMES[state.featuredTheme.id] }}
        </SectionHeadingForHome>
        <div class="o-list__featuredStory">
          <ul class="o-list__featuredStory--items">
            <li
              v-for="story in state.featuredTheme.stories"
              :key="story.id"
              class="o-list__featuredStory--item">
              <div class="l-unit__featuredStory">
                <div
                  v-if="story.thumbImageUrl"
                  class="l-unit__featuredStory--image">
                  <a
                    class="a"
                    :href="storyPath({ id: story.id })">
                    <img
                      class="img"
                      :src="story.thumbImageUrl"
                      loading="lazy"
                      :alt="story.title"
                      data-is-liquid="true">
                  </a>
                <!-- /.l-unit__featuredStory image --></div>

                <div
                  v-if="story.report?.items?.length"
                  class="l-unit__featuredStory--description">
                  <LayoutContentsCardsRows
                    :items="story.report.items"
                    :horizontal="{ pc: true, sp: true }"
                    :heading-options="{ maxLine: 3 }"
                    :has-hero="false"
                    :has-solution-link="false" />
                <!-- /.l-unit__featuredStory description --></div>
              <!-- /.l-unit__featuredStory --></div>
            </li>
          </ul>
        <!-- /.o-list__featuredStory --></div>

        <p class="p u-text__small u-text__right u-margin__larger">
          <a
            class="a u-action__forward"
            :href="themePath({ id: state.featuredTheme.id })">
            {{ THEMES[state.featuredTheme.id] }}をもっと見る
          </a>
        </p>
      <!-- /.f-section__hero body --></div>
    <!-- /.f-section__hero --></section>
  </section>
</template>

<style lang="sass" scoped>
=baseSectionStyle
  $root: &
  position: relative

  &:first-child
    margin-top: 0

  & &--body
    width: 100%
    max-width: #{$global-contents-width-pc}px
    margin: 0 auto

  +breakpoint
    &:first-child
      margin-top: 0

    #{$root}--body
      padding: 0 #{$global-prop-sp}px

// foundation
.f-section__hero
  $root: &
  +baseSectionStyle
  margin: #{$global-margin-pc}px auto

  &:first-child
    margin-top: 0

  &:last-child
    margin-bottom: 0

  +breakpoint
    margin: 20px auto

.f-section__adBanner
  $root: &
  +baseSectionStyle
  margin-top: 30px

  +breakpoint
    margin-top: 20px

.f-section__heroPromotion
  $root: &
  margin: 1em 0 1.5em

  &:first-child
    margin-top: 0

  & &--body
    width: 100%
    max-width: #{$global-contents-width-pc}px
    margin: 0 auto

  +breakpoint
    &:first-child
      margin-top: 1em

.f-section__followCta
  $root: &
  position: relative

  // 単純に body の shadow にすると、body の overflow 設定により hidden になってしまうため、
  // ラッパーの疑似要素で擬似的に shadow を付与
  &::before
    content: ""
    width: 100%
    height: 100%
    display: block
    position: absolute
    top: 0
    left: 50%
    transform: translateX(-50%)
    border-radius: $border-radius
    box-shadow: $drop-bottom-shadow

  & &--body
    min-height: 0
    position: relative
    overflow: hidden

  +breakpoint
    #{$root}--body
      padding:
        top: 1em
        left: #{$global-prop-sp}px
        right: #{$global-prop-sp}px

// layout
.l-unit__heroPromotion
  $root: &
  $image-width-pc: 333
  $image-height-pc: 175
  display: grid
  grid-template-columns: auto 1fr
  grid-template-rows: 1fr auto
  position: relative
  text-decoration: none

  &::before,
  &::after
    content: ""
    width: 100%
    height: 100%
    display: block
    position: absolute
    top: 0
    left: 0
    z-index: 1

  &::before
    background-image: linear-gradient(to top, rgba($bg-black, 0.6) 0%, transparent 70%)

  &::after
    background-color: rgba($bg-x-light-gray, 0)
    visibility: hidden
    +transition

  &[data-label="false"]
    &::before
      display: none

  +mouseover
    &::after
      background-color: rgba($bg-x-light-gray, 0.4)
      visibility: visible

  & &--header
    grid-column: 2 / 3
    grid-row: 2 / 3
    z-index: 1
    padding:
      bottom: 1em
      left: 1em
      right: 1em

  & &--image
    grid-column: 1 / 3
    grid-row: 1 / 3
    position: relative

    &::before
      content: ''
      display: block
      width: 100%
      padding-top: getRelativeValue($image-height-pc, $image-width-pc)

    > .img
      width: 100%
      height: 100%
      position: absolute
      top: 50%
      left: 50%
      transform: translate(-50%, -50%)
      object-fit: cover
      object-position: center top

  & &--footer
    grid-column: 1 / 2
    grid-row: 2 / 3
    padding:
      top: 0.3em
      bottom: 1em
      left: 1em
    z-index: 1

.l-unit__featuredStory
  $root: &
  $image-width-pc: 317
  $image-height-pc: 136
  display: flex
  flex-direction: column

  & &--image
    width: 100%
    position: relative
    margin-bottom: 0.8em

    &:last-child
      margin-bottom: 0

    &::before
      content: ""
      width: 0
      display: block
      padding-top: getRelativeValue($image-height-pc, $image-width-pc)

    > .a
      width: 100%
      height: 100%
      display: block
      +transition

      +mouseover
        opacity: 0.6

      > .img
        width: 100%
        height: 100%
        position: absolute
        top: 50%
        left: 50%
        transform: translate(-50%, -50%)
        object-fit: cover

// object
.o-heading__heroPromotion
  & &--item
    display: -webkit-box
    -webkit-box-orient: vertical
    -webkit-line-clamp: 2
    overflow: hidden
    line-height: 1.4
    color: $font-white
    font:
      size: $font-large
      weight: bold

.o-label__pr
  $root: &

  & &--item
    line-height: 1
    font-size: $font-x-small
    padding: 0.1em 0.3em
    border:
      width: 1px
      style: solid
    border-radius: 0.2em

  &[data-color="white"]
    #{$root}--item
      color: $font-white
      border-color: $border-white

  &[data-color="gray"]
    $color: $font-x-light-gray
    #{$root}--item
      color: $color
      border-color: $color

.o-list__featuredStory
  $root: &
  margin-top: 1em

  &:first-child
    margin-top: 0

  & &--items
    display: grid
    grid-template-columns: repeat(3, 1fr)
    gap: 1em
</style>
